import React from 'react';

import { filesFromDataTransfer } from '~/components/cara-input/data-transfer-files';

/**
 * Returns true when a dragenter/dragover event carries at least one file.
 * Browsers populate `dataTransfer.types` with `'Files'` for file drags but
 * leave `items` empty until `drop` — so we have to check `types` here, not
 * `items.kind`.
 */
const dragHasFiles = (dataTransfer?: DataTransfer | null): boolean => {
  if (!dataTransfer) return false;
  const types = dataTransfer.types;
  if (!types) return false;
  for (let i = 0; i < types.length; i++) {
    if (types[i] === 'Files') return true;
  }
  return false;
};

export type FileDropHandlers = {
  onDragEnter: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
};

/**
 * Drag-and-drop file upload state for a single drop zone.
 *
 * Uses a counter to track nested dragenter/dragleave pairs so child elements
 * inside the zone (textarea, buttons, etc.) don't make `isDragging` flicker
 * as the cursor crosses their boundaries — see
 * https://stackoverflow.com/q/7110353 for the classic version of this bug.
 *
 * Only activates the dragging UI for drags that actually contain files, so
 * selecting text inside the textarea and dragging it doesn't trip the
 * overlay.
 */
export function useFileDrop(args: {
  /** Called with the dropped files when the user releases over the zone. */
  onDrop: (files: File[]) => void;
  /** When true, the zone ignores drag events entirely (e.g. while submitting). */
  disabled?: boolean;
}): {
  isDragging: boolean;
  handlers: FileDropHandlers;
} {
  const { onDrop, disabled } = args;
  const [isDragging, setIsDragging] = React.useState(false);
  const dragCounter = React.useRef(0);

  // Keep the latest `onDrop` in a ref so handlers stay stable.
  const onDropRef = React.useRef(onDrop);
  React.useEffect(() => {
    onDropRef.current = onDrop;
  }, [onDrop]);

  const handlers = React.useMemo<FileDropHandlers>(() => {
    if (disabled) {
      const noop = (e: React.DragEvent) => {
        e.preventDefault();
      };
      return {
        onDragEnter: noop,
        onDragLeave: noop,
        onDragOver: noop,
        onDrop: noop
      };
    }

    return {
      onDragEnter: (e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        dragCounter.current += 1;
        setIsDragging(true);
      },
      onDragLeave: (e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        dragCounter.current = Math.max(0, dragCounter.current - 1);
        if (dragCounter.current === 0) {
          setIsDragging(false);
        }
      },
      onDragOver: (e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        // Required to allow the subsequent `drop` event to fire and to
        // prevent the browser's default "open file in tab" behavior.
        e.preventDefault();
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'copy';
        }
      },
      onDrop: (e) => {
        e.preventDefault();
        dragCounter.current = 0;
        setIsDragging(false);
        const files = filesFromDataTransfer(e.dataTransfer);
        if (files.length > 0) {
          onDropRef.current(files);
        }
      }
    };
  }, [disabled]);

  return { isDragging, handlers };
}
